import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import { CustomModal } from '../CustomModal';
import { Btn, DragDropFileUpload, IconImage, IconPdf, IconVideo2, InputField } from '../ui';
import type { BriefingStyle } from './dailyBriefingData';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

const STYLE_ACCEPT: Record<BriefingStyle, string> = {
    Video: '.mp4,.mov,.webm,.m4v',
    PDF: '.pdf',
    Poster: '.png,.jpg,.jpeg,.gif,.webp',
};

const STYLE_HINT: Record<BriefingStyle, string> = {
    Video: 'MP4, MOV, WEBM, M4V · max 500MB',
    PDF: 'PDF · max 500MB',
    Poster: 'JPG, PNG, GIF, WEBP · max 500MB',
};

interface AddTopicModalProps {
    visible: boolean;
    saving: boolean;
    onDismiss: () => void;
    onSave: (payload: { title: string; file: File }) => Promise<void>;
    /** Opens the Safety & Training upgrade overlay when the module is not enrolled. */
    onUpgrade?: () => void;
}

function fileMatchesStyle(file: File, style: BriefingStyle): boolean {
    const name = file.name.toLowerCase();
    if (style === 'Video') {
        return ['.mp4', '.mov', '.webm', '.m4v'].some((ext) => name.endsWith(ext));
    }
    if (style === 'PDF') {
        return name.endsWith('.pdf');
    }
    return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) => name.endsWith(ext));
}

function inferStyleFromFile(file: File): BriefingStyle {
    const name = file.name.toLowerCase();
    if (['.mp4', '.mov', '.webm', '.m4v'].some((ext) => name.endsWith(ext))) return 'Video';
    if (name.endsWith('.pdf')) return 'PDF';
    return 'Poster';
}

export function AddTopicModal({
    visible,
    saving,
    onDismiss,
    onSave,
    onUpgrade,
}: AddTopicModalProps) {
    const navigate = useNavigate();
    const { company } = useUserContext();
    const hasTrainingModule = Boolean(company?.trainingModuleEnrollment);

    const [style, setStyle] = useState<BriefingStyle>('Video');
    const [duration, setDuration] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!visible) {
            setStyle('Video');
            setDuration('');
            setDescription('');
            setFile(null);
            setError(null);
        }
    }, [visible]);

    const applyFile = (next: File) => {
        if (next.size > MAX_FILE_BYTES) {
            setError('File must be 500MB or smaller.');
            setFile(null);
            return;
        }
        const inferred = inferStyleFromFile(next);
        setStyle(inferred);
        setFile(next);
        setError(null);
        if (!description.trim()) {
            setDescription(next.name.replace(/\.[^/.]+$/, ''));
        }
    };

    const handleSubmit = async () => {
        if (!description.trim()) {
            setError('Description is required.');
            return;
        }
        if (!file) {
            setError('Please choose a file to upload.');
            return;
        }
        if (!fileMatchesStyle(file, style)) {
            setError(`Selected file does not match the ${style} topic style.`);
            return;
        }
        if (file.size > MAX_FILE_BYTES) {
            setError('File must be 500MB or smaller.');
            return;
        }
        setError(null);
        await onSave({ title: description.trim(), file });
    };

    const goTrainingUpsell = (event: React.MouseEvent) => {
        event.preventDefault();
        onDismiss();
        if (hasTrainingModule) {
            navigate('/safety-training');
            return;
        }
        if (onUpgrade) {
            onUpgrade();
            return;
        }
        navigate('/manage/subscription');
    };

    const styleOptions: Array<{ value: BriefingStyle; icon: React.ReactNode }> = [
        { value: 'Video', icon: <IconVideo2 iconClasses="db-add-style-svg" /> },
        { value: 'PDF', icon: <IconPdf iconClasses="db-add-style-svg" /> },
        { value: 'Poster', icon: <IconImage iconClasses="db-add-style-svg" /> },
    ];

    return (
        <CustomModal
            visible={visible}
            onDismiss={onDismiss}
            header="Add Topic to Library"
            size="large"
            bodyClassName="db-add-topic-modal-body"
            footer={
                <div className="db-modal-footer-actions">
                    <Btn
                        variant="secondary"
                        buttonText="Cancel"
                        buttonOnClick={onDismiss}
                        buttonDisabled={saving}
                    />
                    <Btn
                        variant="warning"
                        buttonText={saving ? 'Saving…' : 'Save Topic'}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            }
        >
            <div className="db-add-topic">
                <div className="db-add-note" role="note">
                    <p>
                        Daily Briefing topics are simple. Upload content, give it a name, save. For
                        full training plans with categories and schedules,{' '}
                        <Btn
                            variant="link"
                            buttonText="upgrade to Safety & Training"
                            buttonOnClick={goTrainingUpsell}
                            buttonClasses="db-add-note-link"
                        />
                        .
                    </p>
                </div>

                <div className="db-field">
                    <span className="db-field-label" id="db-add-style-label">
                        Topic style
                    </span>
                    <div
                        className="db-add-style-grid"
                        role="group"
                        aria-labelledby="db-add-style-label"
                    >
                        {styleOptions.map((option) => {
                            const active = style === option.value;
                            return (
                                <Btn
                                    key={option.value}
                                    variant="secondary-outlined"
                                    buttonText={option.value}
                                    buttonIcon={option.icon}
                                    buttonOnClick={() => {
                                        setStyle(option.value);
                                        if (file && !fileMatchesStyle(file, option.value)) {
                                            setFile(null);
                                        }
                                    }}
                                    buttonDisabled={saving}
                                    buttonClasses={`db-add-style-tile db-add-style-tile--${option.value.toLowerCase()}${active ? ' is-active' : ''}`}
                                    buttonAriaProps={{ 'aria-pressed': active }}
                                />
                            );
                        })}
                    </div>
                </div>

                <InputField
                    inputId="db-add-duration"
                    label="Duration (minutes)"
                    type="number"
                    value={duration}
                    onChange={setDuration}
                    placeholder="6"
                    disabled={saving || style !== 'Video'}
                    min={0}
                    step={1}
                    inputMode="numeric"
                />

                <InputField
                    inputId="db-add-description"
                    label="Description"
                    type="text"
                    value={description}
                    onChange={setDescription}
                    placeholder="Electrical Hazard Awareness"
                    disabled={saving}
                    ariaDescribedBy={error ? 'db-add-topic-error' : undefined}
                />

                <div className="db-field">
                    <span className="db-field-label" id="db-add-content-label">
                        Content
                    </span>
                    <DragDropFileUpload
                        inputId="db-add-content-input"
                        labelId="db-add-content-label"
                        fileName={file?.name ?? null}
                        accept={STYLE_ACCEPT[style]}
                        hint={STYLE_HINT[style]}
                        disabled={saving}
                        isUploading={saving}
                        inputAriaLabel="Choose content file"
                        onSelect={applyFile}
                        hasError={Boolean(error) && !file}
                    />
                </div>

                {error ? (
                    <p className="db-modal-error" role="alert" id="db-add-topic-error">
                        {error}
                    </p>
                ) : null}
            </div>
        </CustomModal>
    );
}
